import { useEffect, useLayoutEffect, type RefObject } from 'react';

import {
  useSetTooltipState,
  useTooltipContent,
  useTooltipPinnedStatus,
} from './use-set-tooltip-state.js';

/**
 * Attaches event listeners to the referenced component to enable calling
 * the HideTooltip method whenever a click event occurs outside of it or
 * the escape key is pressed.
 *
 * @param ref - Ref object of the component to attach the event listeners to.
 */
export const useRegisterTooltipHandlerCallbacks = (
  ref: RefObject<HTMLElement | null>,
) => {
  const { setTooltipStatePinned, setTooltipStateVisibility } =
    useSetTooltipState();
  const isTooltipPinned = useTooltipPinnedStatus();
  const hasContent = !!useTooltipContent();

  useLayoutEffect(() => {
    const unpinTooltip = () => setTooltipStatePinned(false);
    const hideTooltip = () => setTooltipStateVisibility(false);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTooltipPinned && event.key === 'Escape') {
        unpinTooltip();
        !hasContent && hideTooltip();
      }
    };

    const handleOutsideClick = (event: MouseEvent) => {
      const currentTarget = event.target;
      const refCurrent = ref.current;
      if (
        isTooltipPinned &&
        refCurrent &&
        !refCurrent.contains(currentTarget as Node)
      ) {
        unpinTooltip();
        !hasContent && hideTooltip();
      }
    };

    if (isTooltipPinned) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('click', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [
    hasContent,
    isTooltipPinned,
    ref,
    setTooltipStatePinned,
    setTooltipStateVisibility,
  ]);

  useEffect(() => {
    return () => {
      setTooltipStatePinned(false);
      setTooltipStateVisibility(false);
    };
  }, [setTooltipStatePinned, setTooltipStateVisibility]);
};
